/**
 * Los iconos propios del proyecto al lado del nombre de un archivo, junto a Ver
 * y Descargar: en las tablas (ShowItemsCompact, ItemsCRUDCompact) y en las
 * vistas de snapshot, que dibujan sus celdas de archivo con
 * useFileCellAffordances (src/entities/common/controls/fileCellAffordances.tsx).
 *
 * Es un asiento del proyecto: el generador lo emite así, sin iconos, y el
 * proyecto lo puede reemplazar en develop con los suyos (por ejemplo, abrir un
 * documento en un visor propio). Mientras el generador no cambie este archivo,
 * la regeneración no pisa la versión del proyecto. Si lo cambia, git mezcla
 * solo lo que no se pisa con lo del proyecto (un campo nuevo, una regla de este
 * comentario) y da conflicto donde los dos cambiaron las mismas líneas o líneas
 * pegadas. Por eso, después de una regeneración que lo cambie, hay que revisar
 * este archivo: los tipos del contrato viven acá para que tsc avise si la
 * implementación del proyecto dejó de calzar con ellos, pero un cambio de regla
 * no lo avisa nadie.
 *
 * Contrato:
 * - useProjectFileAffordances es un hook. useFileCellAffordances lo llama una
 *   vez por tabla o vista, siempre y en el mismo orden; la versión del proyecto
 *   puede usar otros hooks (estado, el root store).
 * - affordancesFor(target) devuelve los iconos de un archivo, en el orden en que
 *   se dibujan; [] es ninguno. Se llama al dibujar cada celda de archivo, y solo
 *   si se sabe cómo bajarlo: con la tabla conectada a un endpoint y
 *   target.download resuelto (en una tabla o vista de snapshot, por el
 *   snapshot: con el uuid del archivo y el registro). Cada icono es un botón
 *   chico con un Tooltip (title), después de Ver y antes de Descargar; su clic
 *   no llega a la fila (no abre además el registro). key distingue a los iconos
 *   de un archivo.
 * - affordancesFor corre mientras se dibuja la celda: tiene que ser pura, sin
 *   hooks ni cambios de estado (un setState ahí hace que la tabla no termine
 *   nunca de dibujarse). El estado se cambia en onClick.
 * - affordancesFor tiene que conservar su identidad entre renders mientras no
 *   cambie lo que devuelve (useCallback): las tablas rehacen sus columnas cada
 *   vez que cambia.
 * - Los iconos salen aunque la tabla no muestre Descargar (enableDownload en
 *   false): target.download dice cómo se baja el archivo, no que la tabla lo
 *   ofrezca.
 * - Salen en filas de cualquier estado (row.__status__ en las grillas de un
 *   formulario): guardada (original), editada sin guardar (original-updated),
 *   nueva (added, added-updated) o marcada para borrar (*-deleted). Qué hacer
 *   con cada una lo decide el proyecto. target.download baja el archivo
 *   guardado del registro: el server lo busca por registro y campo, no por el
 *   nombre de la ruta. Con el archivo cambiado sin guardar, la celda muestra el
 *   nombre nuevo pero la descarga trae el guardado, sin error; se reconoce
 *   porque target.filename no es row.<campo>_data.name. Un archivo recién
 *   subido no llega acá: su <campo>_data no tiene mimetype y la celda no se
 *   dibuja como archivo.
 * - En una vista de snapshot, la fila es la que arma la vista para el campo:
 *   { <campo>: nombre, <campo>_data: lo que guardó el snapshot }.
 * - element es lo que los iconos necesitan dibujar fuera de la celda, por
 *   ejemplo un diálogo. Se monta junto a los visores de Ver, uno por tabla o
 *   vista; null es nada. Un diálogo va en el escalón de esos visores
 *   (zIndex: 10350): con el de MUI por defecto quedaría detrás de la grilla a
 *   pantalla completa de ItemsCRUDCompact (10100) y del popup de una fila
 *   (10200).
 */
import type { ReactNode } from "react";
import type { FileDownloadOptions } from "../utils/downloadWithAuth";
import { useContractFieldReviewAffordances } from "../../entities/signedEdition/contractFields/contractFieldReviewAffordances";

export type ProjectFileAffordanceTarget = {
  row: any;
  fileFieldName: string;
  mimetype: string;
  filename: string;
  /** Cómo se baja este archivo: las mismas opciones que usa Descargar
   *  (inline: false; Ver usa las mismas con inline: true). Tienen la entidad o
   *  el snapshot, el campo, el registro y el nombre o el uuid. */
  download: FileDownloadOptions;
  /** De qué tabla viene: la entidad de las filas (en una vista de snapshot
   *  puede faltar), y la entidad y el registro dueños del snapshot. */
  entityName?: string;
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export type ProjectFileAffordance = {
  key: string;
  title: string;
  icon: ReactNode;
  onClick: () => void;
};

export type ProjectFileAffordances = {
  affordancesFor: (target: ProjectFileAffordanceTarget) => ProjectFileAffordance[];
  element: ReactNode;
};

// Jupiter: el icono «Revisar campos» en el PDF de los documentos de la edición
// y de la edición firmada.
export function useProjectFileAffordances(): ProjectFileAffordances {
  return useContractFieldReviewAffordances();
}
